import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { X, Check, Loader2, User, Camera, Clock, ArrowRight, Scissors } from 'lucide-react'
import { formatRelativeTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'
import { DetectionShot, type Detection, type ShotView } from '@/components/DetectionShot'
import { SplitPersonDrawer } from '@/components/SplitPersonDrawer'

export interface EvidencePerson {
  id: string
  name: string | null
  classificationName: string | null
  totalSightings: number
  firstSeenAt: string | null
  lastSeenAt: string | null
  thumbnailUrl: string | null
}

interface EvidenceSide {
  person: EvidencePerson
  samples: Detection[]
  scanTruncated: boolean
}

interface EvidenceResponse {
  success: boolean
  suggestion: {
    id: string
    faceSim: number | null
    bodySim: number | null
    fusedScore: number | null
    sharedCameras: string[]
    sharedCameraNames: string[]
    timeOverlap: boolean
    gapMinutes: number | null
  }
  source: EvidenceSide
  target: EvidenceSide
}

interface MergeSuggestionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  suggestionId: string | null
  /** Applies the merge (source → target). Throws on failure so the drawer can report it. */
  onMerge: (suggestionId: string) => Promise<void>
  /** Dismisses the suggestion. Throws on failure. */
  onDismiss: (suggestionId: string) => Promise<void>
}

function pct(v: number | null): string {
  return v == null ? '—' : `${Math.round(v * 100)}%`
}

/** One person's column: who they are, then their sampled detections. */
function EvidenceColumn({
  side,
  view,
  role,
  onSplit,
}: {
  side: EvidenceSide
  view: ShotView
  role: 'source' | 'target'
  /** Open the split review for this person, without leaving the comparison. */
  onSplit: (personId: string) => void
}) {
  const { t } = useTranslation()
  const { person, samples } = side

  return (
    <div className="min-w-0">
      <div className="sticky top-0 z-10 -mx-1 mb-2 bg-[rgba(246,241,230,0.98)] px-1 pb-2 pt-1">
        <span
          className={cn(
            'inline-block rounded-pill px-2 py-0.5 text-[10px] font-medium uppercase tracking-eyebrow',
            role === 'target'
              ? 'bg-ink text-paper'
              : 'border border-rule bg-paper-2 text-ink-soft'
          )}
        >
          {role === 'target'
            ? t('mergeSuggestions.compare.kept')
            : t('mergeSuggestions.compare.mergedAway')}
        </span>
        <div className="mt-2 flex items-center gap-2">
          <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg border border-rule bg-ink/5">
            {person.thumbnailUrl ? (
              <img src={person.thumbnailUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <User className="h-5 w-5 text-ink-soft" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-serif text-[15px] font-medium leading-tight text-ink">
              {person.name || t('common.unknown')}
            </p>
            <p className="truncate text-[11px] text-ink-soft">
              {person.classificationName || '—'} ·{' '}
              {t('sightings.count', { count: person.totalSightings })}
            </p>
          </div>
        </div>
        <p className="mt-1 truncate text-[11px] text-ink-soft">
          {t('mergeSuggestions.compare.seenRange', {
            first: formatRelativeTime(person.firstSeenAt, '—'),
            last: formatRelativeTime(person.lastSeenAt, '—'),
          })}
        </p>
        {/* Some of these detections may not be this person at all — that is a
            different fix from merging, and it starts right here. */}
        <button
          type="button"
          onClick={() => onSplit(person.id)}
          className="mt-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-ink-soft transition hover:bg-paper-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Scissors className="h-3 w-3" />
          {t('mergeSuggestions.compare.splitThis')}
        </button>
      </div>

      {samples.length === 0 ? (
        <p className="py-6 text-center text-[12px] text-ink-soft">
          {t('mergeSuggestions.compare.noSamples')}
        </p>
      ) : (
        <div className="space-y-2">
          {samples.map((s, idx) => (
            <DetectionShot key={s.eventId || `${role}-${idx}`} detection={s} view={view} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Side-by-side review panel for one merge suggestion. Follows the feed drawer
 * pattern: right-hand panel on desktop, bottom sheet on mobile.
 */
export function MergeSuggestionDrawer({
  open,
  onOpenChange,
  groupId,
  suggestionId,
  onMerge,
  onDismiss,
}: MergeSuggestionDrawerProps) {
  const { t } = useTranslation()
  const [evidence, setEvidence] = useState<EvidenceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acting, setActing] = useState<'merge' | 'dismiss' | null>(null)
  const [view, setView] = useState<ShotView>('person')
  const [splitPersonId, setSplitPersonId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!groupId || !suggestionId) return
    setLoading(true)
    setError(null)
    try {
      const fn = apiCallable('getMergeSuggestionEvidence')
      const res = await fn({ groupId, suggestionId })
      setEvidence(res.data as EvidenceResponse)
    } catch (e) {
      console.error('Failed to load merge suggestion evidence:', e)
      setError((e as Error)?.message || t('mergeSuggestions.compare.loadError'))
    } finally {
      setLoading(false)
    }
  }, [groupId, suggestionId, t])

  useEffect(() => {
    if (!open) {
      setEvidence(null)
      setError(null)
      setActing(null)
      setNotice(null)
      setSplitPersonId(null)
      return
    }
    load()
  }, [open, load])

  const gapLabel = useMemo(() => {
    const gap = evidence?.suggestion.gapMinutes
    if (gap == null) return null
    if (gap < 1) return t('mergeSuggestions.compare.gapMinutes', { count: 0 })
    if (gap < 60) return t('mergeSuggestions.compare.gapMinutes', { count: Math.round(gap) })
    if (gap < 60 * 24) return t('mergeSuggestions.compare.gapHours', { count: Math.round(gap / 60) })
    return t('mergeSuggestions.compare.gapDays', { count: Math.round(gap / (60 * 24)) })
  }, [evidence, t])

  const act = async (kind: 'merge' | 'dismiss') => {
    if (!suggestionId || acting) return
    setActing(kind)
    setError(null)
    try {
      await (kind === 'merge' ? onMerge(suggestionId) : onDismiss(suggestionId))
      onOpenChange(false)
    } catch (e) {
      console.error(`Merge suggestion ${kind} failed:`, e)
      setError((e as Error)?.message
        || t(kind === 'merge' ? 'mergeSuggestions.mergeError' : 'mergeSuggestions.dismissError'))
    } finally {
      setActing(null)
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-ink/55 backdrop-blur-[4px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[60] flex flex-col bg-[rgba(246,241,230,0.98)] shadow-auvers-modal',
            // Mobile: bottom sheet
            'inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl border-t border-white/70',
            'pb-[env(safe-area-inset-bottom)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            // Desktop ≥sm: right side panel, full height. Wider than the feed
            // drawer because the whole point is two columns side by side.
            'sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[560px] sm:max-h-none',
            'lg:w-[680px]',
            'sm:rounded-none sm:rounded-l-2xl sm:border-l sm:border-t-0',
            'sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right'
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-serif text-[20px] font-medium leading-tight text-ink">
                {t('mergeSuggestions.compare.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-[12px] text-ink-soft">
                {t('mergeSuggestions.compare.subtitle')}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-ink/15 text-ink transition hover:bg-ink/5"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {notice && (
              <Alert className="mb-4">
                <AlertDescription>{notice}</AlertDescription>
              </Alert>
            )}

            {loading && (
              <div className="flex justify-center py-12 text-ink-soft">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {!loading && evidence && (
              <>
                {/* Why this pair was suggested */}
                <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="rounded-md bg-ink px-2 py-1 font-medium text-paper">
                    {t('mergeSuggestions.match')}: {pct(evidence.suggestion.fusedScore)}
                  </span>
                  <span className="rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                    {t('mergeSuggestions.face')} {pct(evidence.suggestion.faceSim)}
                  </span>
                  {evidence.suggestion.bodySim != null && evidence.suggestion.bodySim > 0 && (
                    <span className="rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                      {t('mergeSuggestions.body')} {pct(evidence.suggestion.bodySim)}
                    </span>
                  )}
                  {evidence.suggestion.sharedCameraNames?.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                      <Camera className="h-3 w-3" />
                      {evidence.suggestion.sharedCameraNames.join(', ')}
                    </span>
                  )}
                  {evidence.suggestion.timeOverlap ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                      <Clock className="h-3 w-3" />
                      {t('mergeSuggestions.overlap')}
                    </span>
                  ) : gapLabel ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                      <Clock className="h-3 w-3" />
                      {gapLabel}
                    </span>
                  ) : null}
                </div>

                {/* Zoom toggle */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-ink-soft">
                    {t('mergeSuggestions.compare.hint')}
                  </p>
                  <div className="flex shrink-0 gap-1 rounded-md border border-rule p-0.5">
                    {(['person', 'frame'] as ShotView[]).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setView(v)}
                        className={cn(
                          'rounded-sm px-2 py-1 text-[11px] font-medium transition',
                          view === v ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-paper-2'
                        )}
                      >
                        {v === 'person'
                          ? t('mergeSuggestions.compare.viewPerson')
                          : t('mergeSuggestions.compare.viewFrame')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <EvidenceColumn
                    side={evidence.source}
                    view={view}
                    role="source"
                    onSplit={setSplitPersonId}
                  />
                  <EvidenceColumn
                    side={evidence.target}
                    view={view}
                    role="target"
                    onSplit={setSplitPersonId}
                  />
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 border-t border-rule px-5 py-3">
            <Button className="flex-1" onClick={() => act('merge')} disabled={!!acting || loading}>
              {acting === 'merge' ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-1 h-4 w-4" />
              )}
              {t('mergeSuggestions.compare.confirmMerge')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => act('dismiss')} disabled={!!acting || loading}>
              {acting === 'dismiss' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t('mergeSuggestions.compare.notSame')}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Stacked on top of this panel: splitting is a different fix from
          merging, and the reviewer should not lose the comparison to do it. */}
      <SplitPersonDrawer
        open={splitPersonId !== null}
        onOpenChange={(next) => { if (!next) setSplitPersonId(null) }}
        groupId={groupId}
        personId={splitPersonId}
        onSplit={(result) => {
          // `moved`, not `count`: `count` would send i18next looking for plural
          // variants of this key that do not exist.
          setNotice(t('split.movedNotice', {
            moved: result.detectionsMoved,
            name: result.newPersonName,
          }))
          // Both persons' samples and counts just changed under the comparison.
          load()
        }}
      />
    </DialogPrimitive.Root>
  )
}
